<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { provideSidebarContext, useSidebarResize } from './provider';
import { useAccount } from 'dashboard/composables/useAccount';
import { usePolicy } from 'dashboard/composables/usePolicy';
import { useKbd } from 'dashboard/composables/utils/useKbd';
import { useMapGetter } from 'dashboard/composables/store';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useSidebarKeyboardShortcuts } from './useSidebarKeyboardShortcuts';
import { vOnClickOutside } from '@vueuse/components';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { useWindowSize, useEventListener } from '@vueuse/core';

import Button from 'dashboard/components-next/button/Button.vue';
import SidebarGroup from './SidebarGroup.vue';
import SidebarProfileMenu from './SidebarProfileMenu.vue';
import SidebarAccountSwitcher from './SidebarAccountSwitcher.vue';
import TenantLogo from './TenantLogo.vue';
import Logo from 'next/icon/Logo.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';
import {
  buildClinicalCrmMenuItems,
  buildCommercialContentMenuItem,
} from './clinicalCrmMenu';
import { buildLuciAgentMenuItems } from 'dashboard/routes/dashboard/luciAgent/navigation';
import { CONVERSATION_ASSIGNEE_SCOPES } from 'dashboard/helper/conversationNavigation';
import { ASSIGNEE_TYPE_TAB_PERMISSIONS } from 'dashboard/constants/permissions';
import {
  buildNavigationSections,
  NAVIGATION_SECTIONS,
} from './navigationSections';

const props = defineProps({
  isMobileSidebarOpen: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'closeKeyShortcutModal',
  'openKeyShortcutModal',
  'showCreateAccountModal',
  'closeMobileSidebar',
]);

const { accountScopedRoute } = useAccount();
const { checkPermissions } = usePolicy();
const route = useRoute();
const store = useStore();
const searchShortcut = useKbd([`$mod`, 'k']);
const { t } = useI18n();

const isRTL = useMapGetter('accounts/isRTL');

const { width: windowWidth } = useWindowSize();
const isMobile = computed(() => windowWidth.value < 768);

const accountId = useMapGetter('getCurrentAccountId');
const globalConfig = useMapGetter('globalConfig/get');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);
const productName = computed(
  () => globalConfig.value?.installationName || t('SIDEBAR.PRODUCT_NAME')
);

const hasConversationUnreadCounts = computed(() => {
  return isFeatureEnabledonAccount.value(
    accountId.value,
    FEATURE_FLAGS.CONVERSATION_UNREAD_COUNTS
  );
});

const hasAssignmentV2 = computed(() => {
  return isFeatureEnabledonAccount.value(
    accountId.value,
    FEATURE_FLAGS.ASSIGNMENT_V2
  );
});

const hasClinicalCrmPro = computed(() => {
  return Boolean(
    isFeatureEnabledonAccount.value(accountId.value, FEATURE_FLAGS.CRM) &&
      isFeatureEnabledonAccount.value(
        accountId.value,
        FEATURE_FLAGS.CLINICAL_CRM_PRO
      ) &&
      checkPermissions(['administrator', 'agent', 'crm_analytics'])
  );
});

const hasClinicalCrm = computed(() => {
  return Boolean(
    isFeatureEnabledonAccount.value(accountId.value, FEATURE_FLAGS.CRM) &&
      checkPermissions([
        'administrator',
        'agent',
        'crm_manage',
        'crm_clinical_manage',
      ])
  );
});

const canAccessReports = computed(
  () =>
    isFeatureEnabledonAccount.value(accountId.value, FEATURE_FLAGS.REPORTS) &&
    checkPermissions(['administrator', 'report_manage'])
);

const canAccessHelpResources = computed(
  () =>
    isFeatureEnabledonAccount.value(
      accountId.value,
      FEATURE_FLAGS.HELP_CENTER
    ) && checkPermissions(['administrator', 'knowledge_base_manage'])
);

const hasLuciAgent = computed(() =>
  Boolean(
    isFeatureEnabledonAccount.value(
      accountId.value,
      FEATURE_FLAGS.LUCI_CONVERSATION_CONTROL
    ) && checkPermissions(['administrator'])
  )
);

const canManageClinic = computed(() => checkPermissions(['administrator']));

const fetchConversationUnreadCounts = ([currentAccountId, isEnabled]) => {
  if (!currentAccountId) return;

  if (!isEnabled) {
    store.dispatch('conversationUnreadCounts/clear');
    return;
  }

  store.dispatch('conversationUnreadCounts/get');
};

const toggleShortcutModalFn = show => {
  if (show) {
    emit('openKeyShortcutModal');
  } else {
    emit('closeKeyShortcutModal');
  }
};

useSidebarKeyboardShortcuts(toggleShortcutModalFn);

const expandedItem = ref(null);

const setExpandedItem = name => {
  expandedItem.value = expandedItem.value === name ? null : name;
};

const {
  sidebarWidth,
  isCollapsed,
  setSidebarWidth,
  saveWidth,
  snapToCollapsed,
  snapToExpanded,
  COLLAPSED_THRESHOLD,
} = useSidebarResize();

// On mobile, sidebar is always expanded (flyout mode)
const isEffectivelyCollapsed = computed(
  () => !isMobile.value && isCollapsed.value
);

// Resize handle logic
const isResizing = ref(false);
const startX = ref(0);
const startWidth = ref(0);

provideSidebarContext({
  expandedItem,
  setExpandedItem,
  isCollapsed: isEffectivelyCollapsed,
  sidebarWidth,
  isResizing,
});

// Get clientX from mouse or touch event
const getClientX = event =>
  event.touches ? event.touches[0].clientX : event.clientX;

const onResizeStart = event => {
  isResizing.value = true;
  startX.value = getClientX(event);
  startWidth.value = sidebarWidth.value;
  Object.assign(document.body.style, {
    cursor: 'col-resize',
    userSelect: 'none',
  });
  // Prevent default to avoid scrolling on touch
  event.preventDefault();
};

const onResizeMove = event => {
  if (!isResizing.value) return;

  const delta = isRTL.value
    ? startX.value - getClientX(event)
    : getClientX(event) - startX.value;
  setSidebarWidth(startWidth.value + delta);
};

const onResizeEnd = () => {
  if (!isResizing.value) return;

  isResizing.value = false;
  Object.assign(document.body.style, { cursor: '', userSelect: '' });

  // Snap to collapsed state if below threshold
  if (sidebarWidth.value < COLLAPSED_THRESHOLD) {
    snapToCollapsed();
  } else {
    saveWidth();
  }
};

const onResizeHandleDoubleClick = () => {
  if (isCollapsed.value) snapToExpanded();
  else snapToCollapsed();
};

// Support both mouse and touch events
useEventListener(document, 'mousemove', onResizeMove);
useEventListener(document, 'mouseup', onResizeEnd);
useEventListener(document, 'touchmove', onResizeMove, { passive: false });
useEventListener(document, 'touchend', onResizeEnd);

const conversationCustomViews = useMapGetter(
  'customViews/getConversationCustomViews'
);

onMounted(() => {
  store.dispatch('labels/get');
  store.dispatch('inboxes/get');
  store.dispatch('notifications/unReadCount');
  store.dispatch('teams/get');
  store.dispatch('attributes/get');
  store.dispatch('customViews/get', 'conversation');
  store.dispatch('customViews/get', 'contact');
});

watch([accountId, hasConversationUnreadCounts], fetchConversationUnreadCounts, {
  immediate: true,
});

const closeMobileSidebar = () => {
  if (!props.isMobileSidebarOpen) return;
  emit('closeMobileSidebar');
};

const conversationScopeRoute = scope => {
  const inboxId = route.params.inbox_id;
  const staysInChannel = [
    'inbox_dashboard',
    'conversation_through_inbox',
  ].includes(route.name);

  return staysInChannel && inboxId
    ? accountScopedRoute(
        'inbox_dashboard',
        { inbox_id: inboxId },
        { assignee: scope }
      )
    : accountScopedRoute('home', {}, { assignee: scope });
};

const menuItems = computed(() => {
  const { patientItems, growthItems } = buildClinicalCrmMenuItems({
    t,
    accountScopedRoute,
    hasClinicalCrm: hasClinicalCrm.value,
    hasClinicalCrmPro: hasClinicalCrmPro.value,
    canAccessReports: canAccessReports.value,
  });
  const luciItems = hasLuciAgent.value
    ? buildLuciAgentMenuItems({ t, accountScopedRoute })
    : [];
  const conversationChildren = [
    checkPermissions(ASSIGNEE_TYPE_TAB_PERMISSIONS.all.permissions) && {
      name: 'All',
      label: t('SIDEBAR.ALL_CONVERSATIONS'),
      activeOn: [
        'inbox_conversation',
        'inbox_dashboard',
        'conversation_through_inbox',
      ],
      to: conversationScopeRoute(CONVERSATION_ASSIGNEE_SCOPES.ALL),
    },
    checkPermissions(ASSIGNEE_TYPE_TAB_PERMISSIONS.me.permissions) && {
      name: 'Mine',
      label: t('SIDEBAR.MY_CONVERSATIONS'),
      activeOn: [
        'inbox_conversation',
        'inbox_dashboard',
        'conversation_through_inbox',
      ],
      to: conversationScopeRoute(CONVERSATION_ASSIGNEE_SCOPES.MINE),
    },
    checkPermissions(ASSIGNEE_TYPE_TAB_PERMISSIONS.unassigned.permissions) && {
      name: 'Unassigned',
      label: t('SIDEBAR.UNASSIGNED_CONVERSATIONS'),
      activeOn: [
        'inbox_conversation',
        'inbox_dashboard',
        'conversation_through_inbox',
      ],
      to: conversationScopeRoute(CONVERSATION_ASSIGNEE_SCOPES.UNASSIGNED),
    },
  ].filter(Boolean);

  if (conversationCustomViews.value.length) {
    conversationChildren.push({
      name: 'Saved Views',
      label: t('SIDEBAR.SAVED_VIEWS'),
      icon: 'i-lucide-folder',
      activeOn: ['conversations_through_folders'],
      children: conversationCustomViews.value.map(view => ({
        name: `${view.name}-${view.id}`,
        label: view.name,
        to: accountScopedRoute('folder_conversations', { id: view.id }),
      })),
    });
  }

  const administrationItems = [];

  administrationItems.push(
    buildCommercialContentMenuItem({
      t,
      accountScopedRoute,
      includeClinicalContent: canManageClinic.value && hasClinicalCrm.value,
      includeCampaigns: true,
    })
  );

  if (canManageClinic.value) {
    administrationItems.push(
      {
        name: 'Channels and Integrations',
        label: t('SIDEBAR.CHANNELS_INTEGRATIONS'),
        icon: 'i-lucide-blocks',
        children: [
          {
            name: 'Channel Management',
            label: t('SIDEBAR.CHANNEL_MANAGEMENT'),
            icon: 'i-lucide-mailbox',
            activeOn: [
              'settings_inbox_list',
              'settings_inbox_show',
              'settings_inbox_new',
              'settings_inbox_finish',
              'settings_inboxes_page_channel',
              'settings_inboxes_add_agents',
            ],
            to: accountScopedRoute('settings_inbox_list'),
          },
          {
            name: 'Integration Management',
            label: t('SIDEBAR.INTEGRATION_MANAGEMENT'),
            icon: 'i-lucide-plug',
            to: accountScopedRoute('settings_applications'),
          },
        ],
      },
      {
        name: 'Clinic Settings',
        label: t('SIDEBAR.CLINIC_SETTINGS'),
        icon: 'i-lucide-settings',
        children: [
          {
            name: 'Clinic Account Settings',
            label: t('SIDEBAR.CLINIC_PROFILE'),
            icon: 'i-lucide-building-2',
            to: accountScopedRoute('general_settings_index'),
          },
          {
            name: 'Clinic Agents',
            label: t('SIDEBAR.AGENTS'),
            icon: 'i-lucide-square-user',
            to: accountScopedRoute('agent_list'),
          },
          {
            name: 'Clinic Teams',
            label: t('SIDEBAR.TEAMS'),
            icon: 'i-lucide-users',
            activeOn: [
              'settings_teams_list',
              'settings_teams_new',
              'settings_teams_finish',
              'settings_teams_add_agents',
              'settings_teams_show',
              'settings_teams_edit',
              'settings_teams_edit_members',
              'settings_teams_edit_finish',
            ],
            to: accountScopedRoute('settings_teams_list'),
          },
          ...(hasAssignmentV2.value
            ? [
                {
                  name: 'Clinic Assignment',
                  label: t('SIDEBAR.AGENT_ASSIGNMENT'),
                  icon: 'i-lucide-user-cog',
                  activeOn: [
                    'assignment_policy_index',
                    'agent_assignment_policy_index',
                    'agent_assignment_policy_create',
                    'agent_assignment_policy_edit',
                  ],
                  to: accountScopedRoute('assignment_policy_index'),
                },
              ]
            : []),
          ...(hasClinicalCrm.value
            ? [
                {
                  name: 'Sales Configuration',
                  label: t('SIDEBAR.SALES_CONFIGURATION'),
                  icon: 'i-lucide-sliders-horizontal',
                  to: accountScopedRoute(
                    'clinical_crm_settings',
                    {},
                    { tab: 'funnel' }
                  ),
                },
              ]
            : []),
          {
            name: 'Clinic Labels',
            label: t('SIDEBAR.LABELS'),
            icon: 'i-lucide-tags',
            to: accountScopedRoute('labels_list'),
          },
          {
            name: 'Clinic Custom Attributes',
            label: t('SIDEBAR.CUSTOM_ATTRIBUTES'),
            icon: 'i-lucide-code',
            to: accountScopedRoute('attributes_list'),
          },
          {
            name: 'Clinic Automation',
            label: t('SIDEBAR.AUTOMATION'),
            icon: 'i-lucide-repeat',
            to: accountScopedRoute('automation_list'),
          },
        ],
      }
    );
  }

  if (canAccessHelpResources.value) {
    administrationItems.push({
      name: 'Help Resources',
      label: t('SIDEBAR.HELP_RESOURCES'),
      icon: 'i-lucide-circle-help',
      children: [
        {
          name: 'Help Articles',
          label: t('SIDEBAR.HELP_CENTER.ARTICLES'),
          activeOn: [
            'portals_articles_index',
            'portals_articles_new',
            'portals_articles_edit',
          ],
          to: accountScopedRoute('portals_index', {
            navigationPath: 'portals_articles_index',
          }),
        },
        {
          name: 'Help Categories',
          label: t('SIDEBAR.HELP_CENTER.CATEGORIES'),
          activeOn: [
            'portals_categories_index',
            'portals_categories_articles_index',
            'portals_categories_articles_edit',
          ],
          to: accountScopedRoute('portals_index', {
            navigationPath: 'portals_categories_index',
          }),
        },
        {
          name: 'Help Settings',
          label: t('SIDEBAR.HELP_CENTER.SETTINGS'),
          activeOn: ['portals_settings_index'],
          to: accountScopedRoute('portals_index', {
            navigationPath: 'portals_settings_index',
          }),
        },
      ],
    });
  }

  return [
    {
      name: 'Inbox',
      label: t('SIDEBAR.NOTIFICATION_CENTER'),
      icon: 'i-lucide-inbox',
      to: accountScopedRoute('inbox_view'),
      activeOn: ['inbox_view', 'inbox_view_conversation'],
      getterKeys: {
        count: 'notifications/getUnreadCount',
      },
    },
    {
      name: 'Conversation',
      label: t('SIDEBAR.CONVERSATIONS'),
      icon: 'i-lucide-message-circle',
      children: conversationChildren,
    },
    {
      name: 'Contacts',
      label: t('SIDEBAR.PROSPECTS_PATIENTS'),
      icon: 'i-lucide-contact',
      to: accountScopedRoute(
        'contacts_dashboard_index',
        {},
        { page: 1, search: undefined }
      ),
      activeOn: [
        'contacts_dashboard_index',
        'contacts_dashboard_active',
        'contacts_dashboard_segments_index',
        'contacts_dashboard_labels_index',
        'contacts_edit',
        'contacts_edit_segment',
        'contacts_edit_label',
      ],
    },
    ...patientItems,
    ...growthItems,
    ...luciItems,
    ...administrationItems,
  ];
});

const menuSections = computed(() =>
  buildNavigationSections({
    items: menuItems.value,
    labels: {
      [NAVIGATION_SECTIONS.WORK]: t('SIDEBAR.SECTIONS.WORK'),
      [NAVIGATION_SECTIONS.PATIENTS]: t('SIDEBAR.SECTIONS.PATIENTS'),
      [NAVIGATION_SECTIONS.GROWTH_INTELLIGENCE]: t(
        'SIDEBAR.SECTIONS.GROWTH_INTELLIGENCE'
      ),
      [NAVIGATION_SECTIONS.LUCI_AGENT]: t('SIDEBAR.SECTIONS.LUCI_AGENT'),
      [NAVIGATION_SECTIONS.ADMINISTRATION]: t(
        'SIDEBAR.SECTIONS.ADMINISTRATION'
      ),
    },
  })
);
</script>

<template>
  <aside
    v-on-click-outside="[
      closeMobileSidebar,
      {
        ignore: [
          '#mobile-sidebar-launcher',
          '[data-popover-content]',
          '[data-popover-backdrop]',
        ],
      },
    ]"
    class="bg-n-background flex flex-col text-sm pb-px fixed top-0 ltr:left-0 rtl:right-0 h-full z-40 w-[200px] md:w-auto md:relative md:flex-shrink-0 md:ltr:translate-x-0 md:rtl:translate-x-0 ltr:border-r rtl:border-l border-n-weak"
    :class="[
      {
        'shadow-lg md:shadow-none': isMobileSidebarOpen,
        'ltr:-translate-x-full rtl:translate-x-full': !isMobileSidebarOpen,
        'transition-transform duration-200 ease-out md:transition-[width]':
          !isResizing,
      },
    ]"
    :style="isMobile ? undefined : { width: `${sidebarWidth}px` }"
  >
    <section
      class="grid"
      :class="isEffectivelyCollapsed ? 'mt-3 mb-6 gap-4' : 'mt-1 mb-4 gap-2'"
    >
      <div
        class="flex items-center min-w-0"
        :class="
          isEffectivelyCollapsed
            ? 'justify-center px-1'
            : 'gap-2 px-3 py-1.5 border-b border-n-weak'
        "
      >
        <Logo class="size-5 flex-shrink-0" />
        <span
          v-if="!isEffectivelyCollapsed"
          class="truncate text-sm font-semibold text-n-slate-12"
        >
          {{ productName }}
        </span>
      </div>
      <div
        class="flex gap-2 items-center min-w-0"
        :class="{
          'justify-center px-1': isEffectivelyCollapsed,
          'px-2': !isEffectivelyCollapsed,
        }"
      >
        <template v-if="isEffectivelyCollapsed">
          <SidebarAccountSwitcher
            is-collapsed
            @show-create-account-modal="emit('showCreateAccountModal')"
          />
        </template>
        <template v-else>
          <div class="grid flex-shrink-0 place-content-center size-6">
            <TenantLogo class="size-6" />
          </div>
          <SidebarAccountSwitcher
            class="flex-grow -mx-1 min-w-0"
            @show-create-account-modal="emit('showCreateAccountModal')"
          />
        </template>
      </div>
      <div
        class="flex gap-2"
        :class="isEffectivelyCollapsed ? 'flex-col items-center' : 'px-2'"
      >
        <RouterLink
          v-if="!isEffectivelyCollapsed"
          :to="{ name: 'search' }"
          class="flex gap-2 items-center px-2 py-1 w-full h-7 rounded-lg outline outline-1 outline-n-weak bg-n-button-color transition-all duration-100 ease-out"
        >
          <span class="flex-shrink-0 i-lucide-search size-4 text-n-slate-10" />
          <span class="flex-grow text-start text-n-slate-10">
            {{ t('COMBOBOX.SEARCH_PLACEHOLDER') }}
          </span>
          <span
            class="hidden tracking-wide pointer-events-none select-none text-n-slate-10"
          >
            {{ searchShortcut }}
          </span>
        </RouterLink>
        <RouterLink
          v-else
          :to="{ name: 'search' }"
          class="flex items-center justify-center size-8 rounded-lg outline outline-1 outline-n-weak bg-n-button-color transition-all duration-100 ease-out hover:bg-n-alpha-2 dark:hover:bg-n-slate-9/30"
          :title="t('COMBOBOX.SEARCH_PLACEHOLDER')"
        >
          <span class="i-lucide-search size-4 text-n-slate-11" />
        </RouterLink>
        <ComposeConversation align="start">
          <template #trigger="{ isOpen }">
            <Button
              icon="i-lucide-plus"
              :label="
                isEffectivelyCollapsed ? undefined : t('SIDEBAR.NEW_ITEM')
              "
              color="slate"
              size="sm"
              class="dark:hover:!bg-n-slate-9/30"
              :class="[
                isEffectivelyCollapsed
                  ? '!size-8 !outline-n-weak !text-n-slate-11'
                  : '!h-7 !outline-n-weak !text-n-slate-11',
                { '!bg-n-alpha-2 dark:!bg-n-slate-9/30': isOpen },
              ]"
            />
          </template>
        </ComposeConversation>
      </div>
    </section>
    <nav
      class="grid overflow-y-scroll flex-grow gap-2 pb-5 no-scrollbar min-w-0"
      :class="isEffectivelyCollapsed ? 'px-1' : 'px-2'"
    >
      <ul
        class="flex flex-col gap-1 m-0 list-none min-w-0"
        :class="{ 'items-center': isEffectivelyCollapsed }"
      >
        <template v-for="section in menuSections" :key="section.name">
          <li
            v-if="!isEffectivelyCollapsed"
            class="px-dc-2 pt-dc-3 pb-dc-1 text-dc-label-small uppercase tracking-wide text-n-slate-11"
          >
            {{ section.label }}
          </li>
          <SidebarGroup
            v-for="item in section.items"
            :key="item.name"
            v-bind="item"
          />
        </template>
      </ul>
    </nav>
    <section
      class="flex relative flex-col flex-shrink-0 gap-1 justify-between items-center"
    >
      <div
        class="pointer-events-none absolute inset-x-0 -top-[1.938rem] h-8 bg-gradient-to-t from-n-background to-transparent"
      />
      <div
        class="px-1 py-1.5 flex-shrink-0 flex w-full z-50 gap-2 items-center border-t border-n-weak shadow-[0px_-2px_4px_0px_rgba(27,28,29,0.02)]"
        :class="isEffectivelyCollapsed ? 'justify-center' : 'justify-between'"
      >
        <SidebarProfileMenu
          :is-collapsed="isEffectivelyCollapsed"
          @open-key-shortcut-modal="emit('openKeyShortcutModal')"
        />
      </div>
    </section>
    <!-- Resize Handle (desktop only) -->
    <div
      class="hidden md:block absolute top-0 h-full w-1 cursor-col-resize z-40 ltr:right-0 rtl:left-0 group"
      @mousedown="onResizeStart"
      @touchstart="onResizeStart"
      @dblclick="onResizeHandleDoubleClick"
    >
      <div
        class="absolute top-0 h-full w-px ltr:right-0 rtl:left-0 bg-transparent group-hover:bg-n-brand transition-colors"
        :class="{ 'bg-n-brand': isResizing }"
      />
    </div>
  </aside>
</template>
